import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { getClaimDetails, getWhatHasBeenDamaged, getWorkshopDetails } from './shared/vehicle';
import { isCustomerAllowedToSelectWorkshop } from '../helpers/business-rules/isCustomerAllowedToSelectWorkshop';
import { isCustomerClaimCovered } from '../helpers/business-rules/isCustomerClaimCovered';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';
    const isVehicleAtWorkshop =
        getValueOrDefault(formConfig, formValues, 'fileUpload').customerDidNotUploadReason ===
        'Køretøjet er allerede på værksted';

    const getNextDamagePage = (currentPageId: string): string | undefined => {
        const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);

        switch (currentPageId) {
            case 'hvad-er-skadet':
                if (damageSelections.vehicle.isMyVehicleDamaged) {
                    const isCustomerWithExistingVehiclePolicy =
                        getValueOrDefault(formConfig, formValues, 'registrationNumber').hasVehiclesInSystem === 'yes';

                    // The 'other' vehicle type can be selected in one of two ways:
                    // 1. For a customer with a policy:
                    //    Customer first selects 'Other' in the customerVehicle component, followed up by selecting 'Other' a second time in the radiobuttons list on the next page.
                    //    Because the option selected in the radiobuttons list is not reset if user navigates back in the flow, we also need to check the registrationNumber value.
                    // 2. For a customer without a policy (non policy case):
                    //    The customerVehicle component will render vehicle options where 'Other' is one of the options.
                    const isVehicleTypeOtherSelected = isCustomerWithExistingVehiclePolicy
                        ? getValueOrDefault(formConfig, formValues, 'registrationNumber').registrationNumber === '' &&
                          getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other'
                        : getValueOrDefault(formConfig, formValues, 'registrationNumber').entityType === 'others';

                    // If a customer has selected 'other' as vehicle type -> skip the skadezoner page
                    if (isVehicleTypeOtherSelected) {
                        return 'skader-andet-koeretoej';
                    }

                    return 'skadezoner';
                }
                if (damageSelections.vehicle.isOthersVehicleDamaged) return 'skade-andre-koertoejer';
                if (damageSelections.persons.isDriverDamaged) return 'personskade';
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return 'foerer';
            case 'skader-andet-koeretoej':
            case 'skadezoner':
                if (damageSelections.vehicle.isOthersVehicleDamaged) return 'skade-andre-koertoejer';
                if (damageSelections.persons.isDriverDamaged) return 'personskade';
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return 'foerer';
            case 'skade-andre-koertoejer':
                const isSingleCounterpartVehicleDamaged =
                    getValueOrDefault(formConfig, formValues, 'howManyOtherVehiclesAreDamaged').value ===
                    'aSingleVehicle';

                if (isSingleCounterpartVehicleDamaged) {
                    return 'anden-persons-koertoej';
                }

                if (damageSelections.persons.isDriverDamaged) return 'personskade';
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return 'foerer';
            case 'anden-persons-koertoej':
                if (damageSelections.persons.isDriverDamaged) return 'personskade';
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return 'foerer';
            case 'personskade':
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return 'foerer';
            case 'oplysninger-om-passagerer':
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return 'foerer';
        }
    };

    switch (curPageId) {
        case 'godt-at-vide':
            nextPage = 'dato';
            break;
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'vaelg-koeretoej';
            break;

        case 'vaelg-koeretoej':
            const registratioNumberField = getValueOrDefault(formConfig, formValues, 'registrationNumber');
            const { registrationNumber } = registratioNumberField;

            // If no registationNumber.registrationNumber alway to andet-koeretoej-nummerplade
            if (registrationNumber === '') {
                nextPage = 'andet-koeretoej-nummerplade';
            } else {
                nextPage = 'situation';
            }
            break;
        case 'andet-koeretoej-nummerplade':
            nextPage = 'om-andet-koeretoej';
            break;

        case 'om-andet-koeretoej':
            nextPage = 'situation';
            break;
        case 'situation':
            if (
                getValueOrDefault(formConfig, formValues, 'whichStatementMostAccuratelyDescribesTheSituation').value ===
                    'myCarHalted' ||
                getValueOrDefault(formConfig, formValues, 'whichStatementMostAccuratelyDescribesTheSituation').value ===
                    'noneOfTheAbove'
            ) {
                nextPage = 'ansvar';
            } else {
                nextPage = 'hvad-er-skadet';
            }
            break;

        case 'ansvar':
            nextPage = 'hvad-er-skadet';
            break;

        //page 7  Who or what is damanged
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'skadezoner':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'skade-andre-koertoejer':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'anden-persons-koertoej':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId);
            break;

        //page 7E - skade-ting
        case 'skade-ting':
            if (getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes') {
                nextPage = 'genstandsregistrering';
            } else {
                nextPage = 'foerer';
            }
            break;
        case 'genstandsregistrering':
            nextPage = 'foerer';
            break;

        case 'foerer':
            if (getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value === 'myPartner') {
                nextPage = 'foerer-oplysninger-1';
            } else if (
                getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value === 'someoneFromMyHousehold'
            ) {
                nextPage = 'foerer-oplysninger-2';
            } else if (
                getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value === 'someoneOutsideOfMyHousehold'
            ) {
                nextPage = 'foerer-oplysninger-3';
            } else {
                nextPage = 'paavirkning';
            }

            break;
        case 'foerer-oplysninger-1':
            nextPage = 'paavirkning';
            break;
        case 'foerer-oplysninger-2':
            nextPage = 'paavirkning';
            break;
        case 'foerer-oplysninger-3':
            nextPage = 'paavirkning';
            break;
        case 'paavirkning':
            nextPage = 'oplysninger-om-modpart';
            break;
        case 'oplysninger-om-modpart':
            if (getValueOrDefault(formConfig, formValues, 'doYouHaveInformationAboutOthersInvolved').value === 'yes') {
                nextPage = 'modpartens-rolle-1';
            } else {
                nextPage = 'dokumentation';
            }
            break;
        //page 10 - modpart
        case 'modpartens-rolle-1':
            nextPage = 'om-modparten-1';
            break;
        case 'modpartens-rolle-2':
            nextPage = 'om-modparten-2';
            break;
        case 'modpartens-rolle-3':
            nextPage = 'om-modparten-3';
            break;
        //Page 10B: Counterpart (details, person 1-4)
        case 'om-modparten-1':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson2',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-2'
                : 'dokumentation';
            break;
        case 'om-modparten-2':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson3',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-3'
                : 'dokumentation';
            break;
        case 'om-modparten-3':
            nextPage = 'dokumentation';
            break;

        case 'dokumentation':
            nextPage = 'politi-og-samtykke';
            break;

        case 'politi-og-samtykke':
            if (getValueOrDefault(formConfig, formValues, 'policeContacted').value === 'Y') {
                nextPage = 'samtykke';
            } else if (
                isCustomerClaimCovered(
                    getClaimDetails(formConfig, formValues),
                    getWorkshopDetails(formConfig, formValues)
                ) &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'du-er-daekket';
            } else if (
                isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr') &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'hvilken-skade':
            if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'om-dig';
            }
            break;
        case 'samtykke':
            if (
                isCustomerClaimCovered(
                    getClaimDetails(formConfig, formValues),
                    getWorkshopDetails(formConfig, formValues)
                ) &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'du-er-daekket';
            } else if (
                isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr') &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'du-er-daekket':
            nextPage = 'hvilken-skade';
            break;

        /*       case 'vaerkstedsvalg':
            if (getValueOrDefault(formConfig, formValues, 'selectWorkshop').scenario !== 'customerDoesNotWantShops') {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'om-dig';
            }
            break; */
        case 'find-vaerksted':
            nextPage = 'om-dig';
            break;

        case 'om-dig':
            nextPage = 'resume';
            break;

        case 'resume':
            nextPage = undefined;
            break;

        default:
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Determines if a given 'add another person' checkbox element is selected or not.
 * @param elementId Id of the 'add another person' checkbox element
 * @param formConfig
 * @param formValues
 * @returns True if the 'add another person' checkbox is selected, else false.
 */
const isAddAnotherPersonChecked = (elementId: string, formConfig: FormConfig, formValues: FormValues): boolean => {
    const addAnotherPersonCheckbox = getValueOrDefault(formConfig, formValues, elementId);
    return addAnotherPersonCheckbox && addAnotherPersonCheckbox.length > 0
        ? addAnotherPersonCheckbox[0].value === 'yes'
        : false;
};
